// src/components/CompetitionWorkouts.js
import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    IconButton,
    Dialog,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Alert
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { deleteCompetitionWorkout } from '../api/competitionApi';
import AddWorkoutDialog from './AddWorkoutDialog';

export default function CompetitionWorkouts({ competition, isAdminView, onUpdate }) {
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [editingWorkout, setEditingWorkout] = useState(null);
    const [selectedWorkout, setSelectedWorkout] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(0);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [workoutToDelete, setWorkoutToDelete] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    const handleDeleteClick = (workoutId) => {
        setWorkoutToDelete(workoutId);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setWorkoutToDelete(null);
    };

    const handleDeleteWorkout = async () => {
        try {
            setErrorMessage('');
            await deleteCompetitionWorkout(workoutToDelete);
            setDeleteConfirmOpen(false);
            setWorkoutToDelete(null);
            onUpdate();
        } catch (error) {
            console.error('Error deleting workout:', error);
            setErrorMessage('Failed to delete workout');
            setDeleteConfirmOpen(false);
        }
    };

    const handleEditWorkout = (workout) => {
        setEditingWorkout(workout);
        setOpenAddDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenAddDialog(false);
        setEditingWorkout(null);
        onUpdate();
    };

    const getResultTypeLabel = (type) => {
        switch (type) {
            case 'TIME':
                return 'Time (hh:mm:ss)';
            case 'REPS':
                return 'Repetitions';
            case 'WEIGHT':
                return 'Weight (kg)';
            default:
                return type;
        }
    };

    return (
        <Box>
            {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                    {errorMessage}
                </Alert>
            )}

            {isAdminView && (
                <Box sx={{ mb: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setOpenAddDialog(true)}
                        fullWidth
                    >
                        Add Workout
                    </Button>
                </Box>
            )}

            {/* Global Category Selection */}
            {competition.categories && competition.categories.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={selectedCategory}
                            label="Category"
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            {competition.categories.map((category, idx) => (
                                <MenuItem key={idx} value={idx}>
                                    {category}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            )}

            {competition.workouts && competition.workouts.length === 0 ? (
                <Card>
                    <CardContent>
                        <Typography align="center" color="textSecondary">
                            No workouts added yet
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {competition.workouts && competition.workouts.map((workout, index) => (
                        <Card key={workout.id}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Box>
                                        <Typography variant="h6" fontWeight="bold">
                                            Workout #{index + 1}: {workout.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={getResultTypeLabel(workout.resultType)}
                                                size="small"
                                                color="primary"
                                            />
                                            {isAdminView && workout.tiebreak && (
                                                <Chip
                                                    label={`Tiebreak: ${workout.tiebreakType === 'TIME' ? 'Time' : workout.tiebreakType === 'NUMBER' ? 'Number' : 'N/A'} (${workout.tiebreakLowerBetter ? 'Lower is better' : 'Higher is better'})`}
                                                    size="small"
                                                    color="secondary"
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                    {isAdminView && (
                                        <Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditWorkout(workout)}
                                                color="primary"
                                            >
                                                <Edit />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteClick(workout.id)}
                                                color="error"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>

                                {/* Workout description for selected category */}
                                <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {workout.workoutDescriptions[competition.categories[selectedCategory]] || 'No description for this category'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}

            {/* Add/Edit Workout Dialog */}
            {isAdminView && (
                <AddWorkoutDialog
                    open={openAddDialog}
                    onClose={handleCloseDialog}
                    competitionId={competition.id}
                    categories={competition.categories}
                    workout={editingWorkout}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="delete-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="delete-dialog-description"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                    >
                        Are you sure you want to delete this workout? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handleDeleteCancel}
                        color="primary"
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteWorkout}
                        color="error"
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}